import React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormLabel from '@mui/material/FormLabel';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import type {
  MrChatAnswerValue,
  MrChatField,
  MrChatLineItem,
} from './types';

type Props = {
  field: MrChatField;
  label: string;
  required: boolean;
  value: MrChatAnswerValue | undefined;
  onChange: (value: MrChatLineItem[]) => void;
};

function text(value: unknown, fallback = ''): string {
  return typeof value === 'string' || typeof value === 'number'
    ? String(value)
    : fallback;
}

function keyFor(field: MrChatField): string {
  return typeof field.key === 'string' ? field.key : '';
}

function labelFor(field: MrChatField): string {
  return text(field.label ?? field.question, keyFor(field));
}

function requiredField(field: MrChatField): boolean {
  return field.required === true || field.isRequired === true;
}

function lineItems(value: MrChatAnswerValue | undefined): MrChatLineItem[] {
  return Array.isArray(value)
    ? (value as unknown[]).filter(
      (item: unknown): item is MrChatLineItem => Boolean(item)
        && typeof item === 'object'
        && !Array.isArray(item),
    )
    : [];
}

function blankItem(
  fields: MrChatField[],
  existingRows: MrChatLineItem[],
): MrChatLineItem {
  const item = Object.fromEntries(fields.flatMap((field) => {
    const key = keyFor(field);
    if (!key) return [];
    const initial = field.default ?? field.value;
    return [[key, typeof initial === 'string' || typeof initial === 'number'
      ? initial
      : '']];
  }));
  if (!fields.some((field) => keyFor(field) === 'clin_number')) return item;

  const used = new Set(existingRows.map((row) => (
    text(row.clin_number).trim()
  )).filter(Boolean));
  const initial = text(item.clin_number).trim() || '0001';
  if (!used.has(initial)) {
    item.clin_number = initial;
    return item;
  }

  if (/^\d+$/.test(initial)) {
    const usedNumbers = Array.from(used)
      .filter((value) => /^\d+$/.test(value))
      .map((value) => Number(value));
    let next = Math.max(Number(initial), ...usedNumbers) + 1;
    const width = Math.max(4, initial.length);
    let candidate = String(next).padStart(width, '0');
    while (used.has(candidate)) {
      next += 1;
      candidate = String(next).padStart(width, '0');
    }
    item.clin_number = candidate;
    return item;
  }

  let suffix = 2;
  while (used.has(`${initial}-${suffix}`)) suffix += 1;
  item.clin_number = `${initial}-${suffix}`;
  return item;
}

function isMissing(value: unknown): boolean {
  return value === undefined
    || value === null
    || (typeof value === 'string' && value.trim() === '');
}

/**
 * Render the server-issued pricing schedule without guessing its columns.
 *
 * Hidden canonical properties on an existing row are preserved during local
 * edits, while the browser exposes only the columns named in ``item_fields``.
 */
const MrChatLineItemsField: React.FC<Props> = ({
  field,
  label,
  required,
  value,
  onChange,
}) => {
  const columns = (field.item_fields || []).filter((column) => keyFor(column));
  const rows = lineItems(value);
  const help = text(field.help_text ?? field.description);
  const nextRowKey = React.useRef(0);
  const rowKeys = React.useRef<string[]>([]);
  while (rowKeys.current.length < rows.length) {
    rowKeys.current.push(`line-item-${nextRowKey.current}`);
    nextRowKey.current += 1;
  }
  if (rowKeys.current.length > rows.length) {
    rowKeys.current.length = rows.length;
  }

  if (!columns.length) {
    return (
      <Alert severity="warning" data-testid="mr-chat-line-items-unavailable">
        The pricing schedule definition is unavailable. Refresh this chat
        before continuing.
      </Alert>
    );
  }

  return (
    <Box
      component="fieldset"
      aria-label={label}
      data-testid="mr-chat-line-items"
      sx={{ border: 0, m: 0, p: 0, minWidth: 0 }}
    >
      <Typography component="legend" variant="body2" sx={{ fontWeight: 600 }}>
        {label} ({required ? 'Required' : 'Optional'})
      </Typography>
      {help && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 0.5 }}
        >
          {help}
        </Typography>
      )}
      <Stack spacing={1.25} sx={{ mt: 1 }}>
        {rows.map((row, rowIndex) => (
          <Paper
            key={rowKeys.current[rowIndex]}
            variant="outlined"
            data-testid={`mr-chat-line-item-${rowIndex}`}
            sx={{ p: 1.5, minWidth: 0 }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
              sx={{ mb: 1.25 }}
            >
              <Typography variant="subtitle2">Line item {rowIndex + 1}</Typography>
              <Button
                size="small"
                color="error"
                aria-label={`Remove line item ${rowIndex + 1}`}
                onClick={() => {
                  rowKeys.current.splice(rowIndex, 1);
                  onChange(rows.filter((_, index) => index !== rowIndex));
                }}
              >
                Remove
              </Button>
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'minmax(0, 1fr)',
                  md: 'repeat(3, minmax(0, 1fr))',
                },
                gap: 1.25,
              }}
            >
              {columns.map((column) => {
                const key = keyFor(column);
                const columnLabel = labelFor(column);
                const columnRequired = requiredField(column);
                const inputType = column.input_type === 'number'
                  ? 'number'
                  : 'text';
                const multiline = column.input_type === 'textarea';
                const current = row[key as keyof MrChatLineItem];
                const inputId = `mr-chat-${rowKeys.current[rowIndex]}-${key}`;
                return (
                  <Box key={key} sx={{ gridColumn: key === 'description'
                    || key === 'attachment_reference' ? { md: 'span 3' } : undefined }}>
                    <FormLabel component="label" htmlFor={inputId} required={columnRequired} sx={{ display: 'block', mb: 0.5, fontSize: '0.875rem' }}>
                      {columnLabel}
                    </FormLabel>
                    <TextField
                      id={inputId}
                      fullWidth
                      size="small"
                      required={columnRequired}
                      value={text(current)}
                      type={inputType}
                      multiline={multiline}
                      minRows={multiline ? 2 : undefined}
                      error={columnRequired && isMissing(current)}
                      helperText={text(column.help_text ?? column.description)}
                      onChange={(event) => {
                        const updated = [...rows];
                        updated[rowIndex] = {
                          ...row,
                          [key]: event.target.value,
                        };
                        onChange(updated);
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          </Paper>
        ))}
      </Stack>
      {!rows.length && (
        <Alert severity={required ? 'warning' : 'info'} sx={{ mt: 1 }}>
          Add at least one pricing-schedule line item.
        </Alert>
      )}
      <Button
        variant="outlined"
        size="small"
        sx={{ mt: 1.25 }}
        onClick={() => {
          rowKeys.current.push(`line-item-${nextRowKey.current}`);
          nextRowKey.current += 1;
          onChange([...rows, blankItem(columns, rows)]);
        }}
      >
        Add line item
      </Button>
    </Box>
  );
};

export default MrChatLineItemsField;
